import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { createGlobalStyle } from 'styled-components';
import ProgramSectionView from './ProgramSectionView';

// The program as a PDF, on a static copy of the public program (ProgramSectionView in print mode)
// rendered into its own root at the end of <body>. While it prints, everything else on the page is
// hidden, so the PDF holds the program alone, on A4, without the page, buttons, cards or animations
// around it. Two printers use it: the admin's browser (ProgramPrint, "Save as PDF" in the print dialog)
// and the server's Chromium, which opens the print page (pages/ProgramPrintPage.js) and waits for
// window.programPrintReady.

const ROOT_ID = 'program-print-root';

const PrintStyles = createGlobalStyle`
  @media screen {
    #${ROOT_ID} { display: none; }
  }

  @media print {
    /* the website's light blue on the whole sheet, margins included (Chromium browsers; others
       print white margins around it) */
    @page { size: A4; margin: 14mm 12mm 16mm; background: #d6e8f4; }

    html, body.program-printing { background: transparent !important; }
    body.program-printing > *:not(#${ROOT_ID}) { display: none !important; }

    #${ROOT_ID} {
      font-family: Roboto, Arial, sans-serif;
      font-size: 9.5pt;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
  }
`;

// Waits until every image of the print copy has loaded (or failed), so the PDF has the photos; a slow
// image server holds the print dialog back for IMAGE_WAIT_MS at most.
const IMAGE_WAIT_MS = 4000;
const imagesSettled = (root) => Promise.race([
  Promise.all([...root.querySelectorAll('img')].map((img) => (
    img.complete ? Promise.resolve() : new Promise((resolve) => {
      img.addEventListener('load', resolve, { once: true });
      img.addEventListener('error', resolve, { once: true });
    })
  ))),
  new Promise((resolve) => { setTimeout(resolve, IMAGE_WAIT_MS); }),
]);

// The print copy of the program at the end of <body>: rendered, its images awaited, then the page is
// switched to print it alone (body.program-printing) and onReady is called. Used by ProgramPrint (the
// admin's print dialog) and by the print page the server's PDF renderer opens (ProgramPrintPage).
export const ProgramPrintCopy = ({ program, people, language, title, onReady }) => {
  const [root, setRoot] = useState(null);

  useEffect(() => {
    const el = document.createElement('div');
    el.id = ROOT_ID;
    document.body.appendChild(el);
    setRoot(el);
    return () => {
      document.body.classList.remove('program-printing');
      el.remove();
    };
  }, []);

  useEffect(() => {
    if (!root) return undefined;
    let cancelled = false;
    imagesSettled(root).then(() => {
      if (cancelled) return;
      document.body.classList.add('program-printing');
      onReady();
    });
    return () => { cancelled = true; };
  }, [root, onReady]);

  if (!root) return null;
  return createPortal(
    <>
      <PrintStyles />
      <ProgramSectionView program={program} people={people} language={language} title={title} print />
    </>,
    root,
  );
};

// Mounted while the admin's print runs: the print copy, the browser's print dialog once it is ready,
// and onDone when the dialog closes.
const ProgramPrint = ({ program, people, language, title, onDone }) => {
  useEffect(() => {
    window.addEventListener('afterprint', onDone, { once: true });
    return () => window.removeEventListener('afterprint', onDone);
  }, [onDone]);

  // Browsers that print synchronously have already fired afterprint when print() returns.
  const print = useCallback(() => window.print(), []);

  return <ProgramPrintCopy program={program} people={people} language={language} title={title} onReady={print} />;
};

export default ProgramPrint;
